/**
 * Line Renderer
 *
 * Handles rendering of detected lines as 3D objects in the scene
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { DetectedLine } from './types';
import { Viewer } from '../../../../viewer/viewer';

const BRAND_GREEN = 0x59cd90; // Brand green color for preview lines

export class LineRenderer {
  private viewer: Viewer;
  private lineObjects: Map<string, Line2> = new Map();
  private lineGroup: THREE.Group;
  private raycaster: THREE.Raycaster;
  private localOrigin: THREE.Vector3 = new THREE.Vector3();

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.raycaster = new THREE.Raycaster();
    this.raycaster.params.Line2 = { threshold: 0.1 }; // 10cm threshold for line picking

    // Create group to hold all line objects
    this.lineGroup = new THREE.Group();
    this.lineGroup.name = 'DetectedLines';
    this.viewer.scene.scene.add(this.lineGroup);
  }

  /**
   * Render detected lines as 3D objects
   * Uses local coordinate system to avoid floating point precision issues
   * when coordinates are far from world origin.
   */
  render(lines: DetectedLine[]): void {
    // Clear existing lines that are no longer present
    const currentLineIds = new Set(lines.map((line) => line.id));
    for (const [id, lineObject] of this.lineObjects.entries()) {
      if (!currentLineIds.has(id)) {
        this.lineGroup.remove(lineObject);
        lineObject.geometry.dispose();
        (lineObject.material as LineMaterial).dispose();
        this.lineObjects.delete(id);
      }
    }

    // Calculate local origin from first line to avoid precision issues
    // Position the group at this origin, then use relative coordinates for lines
    if (lines.length > 0) {
      this.localOrigin.copy(lines[0].start);
      this.lineGroup.position.copy(this.localOrigin);
    } else {
      this.localOrigin.set(0, 0, 0);
      this.lineGroup.position.set(0, 0, 0);
    }

    // Render each line using local coordinates
    for (const line of lines) {
      this.renderLine(line);
    }
  }

  /**
   * Render a single line as a 3D object
   * Uses local coordinates relative to lineGroup position for precision
   */
  private renderLine(line: DetectedLine): void {
    let lineObject = this.lineObjects.get(line.id);

    if (!lineObject) {
      // Create new thick line using Line2
      const geometry = new LineGeometry();
      const material = new LineMaterial({
        linewidth: 5, // In pixels - thick lines!
        depthTest: false,
        depthWrite: false,
        transparent: true,
        resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      });

      lineObject = new Line2(geometry, material);
      lineObject.userData.lineId = line.id;
      lineObject.renderOrder = 999; // Render on top
      this.lineGroup.add(lineObject);
      this.lineObjects.set(line.id, lineObject);
    }

    // Convert world positions to local coordinates (relative to lineGroup position)
    // This avoids floating point precision issues when far from world origin
    const localStart = line.start.clone().sub(this.localOrigin);
    const localEnd = line.end.clone().sub(this.localOrigin);

    // Update geometry with local positions
    // Line2 uses a different format: [x1, y1, z1, x2, y2, z2]
    const positions = [localStart.x, localStart.y, localStart.z, localEnd.x, localEnd.y, localEnd.z];
    (lineObject.geometry as LineGeometry).setPositions(positions);

    // Update material based on state
    const material = lineObject.material as LineMaterial;

    // Update resolution in case viewport resized
    material.resolution.set(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight);

    if (line.isSelected) {
      material.color.setHex(BRAND_GREEN); // Brand green for selected
      material.opacity = 1.0;
      material.linewidth = 8; // Extra thick when selected
      lineObject.visible = true;
    } else if (line.isHovered) {
      // Hovered state - hide the line (isolation plane is shown instead)
      lineObject.visible = false;
    } else if (line.isHidden) {
      // Explicitly hidden (e.g., when shift is pressed for manual drawing)
      lineObject.visible = false;
    } else {
      // Default state - preview lines are always visible
      material.color.setHex(BRAND_GREEN); // Brand green
      material.opacity = 0.7;
      material.linewidth = 5;
      lineObject.visible = true;
    }

    material.needsUpdate = true;
  }

  /**
   * Get line at screen position (for hover/click detection)
   * Uses screen-space distance calculation with current projected positions
   * to ensure accurate hit detection regardless of camera movement.
   */
  getLineAtPosition(x: number, y: number, lines: DetectedLine[]): DetectedLine | null {
    const camera = this.viewer.scene.getActiveCamera();
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const viewportHeight = this.viewer.renderArea.clientHeight;

    const clickPoint = new THREE.Vector2(x, y);
    const threshold = 15; // pixels - maximum distance from line to register a hit

    let closestLine: DetectedLine | null = null;
    let closestDistance = threshold;

    for (const line of lines) {
      // Project world coordinates to current screen position
      const screenStart = this.projectToScreen(line.start, camera, viewportWidth, viewportHeight);
      const screenEnd = this.projectToScreen(line.end, camera, viewportWidth, viewportHeight);

      // Calculate distance from click point to line segment in screen space
      const distance = this.pointToLineSegmentDistance(clickPoint, screenStart, screenEnd);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestLine = line;
      }
    }

    return closestLine;
  }

  /**
   * Get line whose sphere (midpoint) is at screen position
   * Only returns a line if the click is close to the sphere at the midpoint
   */
  getLineAtSpherePosition(x: number, y: number, lines: DetectedLine[]): DetectedLine | null {
    const camera = this.viewer.scene.getActiveCamera();
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const viewportHeight = this.viewer.renderArea.clientHeight;

    const clickPoint = new THREE.Vector2(x, y);
    const threshold = 15; // pixels - maximum distance from sphere center to register a hit

    let closestLine: DetectedLine | null = null;
    let closestDistance = threshold;

    for (const line of lines) {
      // Calculate midpoint in world space
      const midpoint = line.start.clone().add(line.end).multiplyScalar(0.5);

      // Project midpoint to screen position
      const screenMidpoint = this.projectToScreen(midpoint, camera, viewportWidth, viewportHeight);

      // Calculate distance from click point to midpoint
      const distance = clickPoint.distanceTo(screenMidpoint);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestLine = line;
      }
    }

    return closestLine;
  }

  /**
   * Get the world position of a line's midpoint (where the sphere is)
   */
  getLineMidpoint(line: DetectedLine): THREE.Vector3 {
    return line.start.clone().add(line.end).multiplyScalar(0.5);
  }

  /**
   * Project a 3D world point to 2D screen coordinates
   */
  private projectToScreen(worldPoint: THREE.Vector3, camera: THREE.Camera, viewportWidth: number, viewportHeight: number): THREE.Vector2 {
    const projected = worldPoint.clone().project(camera);
    return new THREE.Vector2(((projected.x + 1) * viewportWidth) / 2, ((-projected.y + 1) * viewportHeight) / 2);
  }

  /**
   * Calculate the shortest distance from a point to a line segment
   */
  private pointToLineSegmentDistance(point: THREE.Vector2, lineStart: THREE.Vector2, lineEnd: THREE.Vector2): number {
    const lineVec = new THREE.Vector2().subVectors(lineEnd, lineStart);
    const lineLength = lineVec.length();

    if (lineLength < 0.0001) {
      return point.distanceTo(lineStart);
    }

    // Normalize the line direction
    const lineDir = lineVec.clone().normalize();

    // Vector from line start to point
    const toPoint = new THREE.Vector2().subVectors(point, lineStart);

    // Project point onto line
    const t = toPoint.dot(lineDir);

    // Clamp to segment
    const clampedT = Math.max(0, Math.min(lineLength, t));

    // Get closest point on segment
    const closestPoint = lineStart.clone().add(lineDir.multiplyScalar(clampedT));

    return point.distanceTo(closestPoint);
  }

  /**
   * Clear all rendered lines
   */
  clear(): void {
    for (const lineObject of this.lineObjects.values()) {
      this.lineGroup.remove(lineObject);
      lineObject.geometry.dispose();
      (lineObject.material as LineMaterial).dispose();
    }
    this.lineObjects.clear();

    // Reset local origin and group position
    this.localOrigin.set(0, 0, 0);
    this.lineGroup.position.set(0, 0, 0);
  }

  /**
   * Cleanup and remove
   */
  dispose(): void {
    this.clear();
    this.viewer.scene.scene.remove(this.lineGroup);
  }
}
